import React, { useEffect } from 'react';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import 'react-loading-skeleton/dist/skeleton.css';

import { TSegmentedBannerAlias } from '@r1-frontend/api-domru/spec-offer/v1/banners/dto/responseDto';

import { WithSkeleton, WithSkeletonContext } from '@r1-frontend/ui-react/components/loaders/Skeleton';
import { H3, H4 } from '@r1-frontend/ui-react/components/typography/heading';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';

import SpeedBonusConnection from '~/src/entities/speed-bonus';
import { specialOfferAnalytics } from '~/src/features/specialOffersContainer/analytics';
import DefaultPromoStub from '~/src/features/specialOffersContainer/components/DefaultPromoStub';
import LkPromotionalCardsCarousel from '~/src/features/specialOffersContainer/containers/Carousel';
import { setIsPopupOpened, setSelectedOfferId } from '~/src/features/specialOffersContainer/store/actions';
import { loadOffers } from '~/src/features/specialOffersContainer/store/actions/thunk';
import { TState } from '~/src/store';

import * as ST from './styled';

interface IOffersProps {
    title: string,
    showAllButton?: boolean,
    headlined?: boolean,
    root?: TSegmentedBannerAlias,
    EmptyComponent?: () => JSX.Element,
}

const SpecialOffersContainer = ({
    title,
    showAllButton,
    headlined,
    root,
    EmptyComponent,
}: IOffersProps) => {
    const dispatch = useDispatch();

    const { isLoading, offers } = useSelector((state: TState) => state.specOffers, shallowEqual);

    const { query: { offer_id, requestId } } = useRouter<string>();

    useEffect(() => {
        dispatch(loadOffers(true));
    }, []);

    useEffect(() => {
        const offerByQuery = offers.find(offer => offer.requestId === Number(requestId) || offer.id === Number(offer_id));

        if (offerByQuery) {
            specialOfferAnalytics.showPromoDetails(offerByQuery);

            if (offerByQuery.isShowNewDetail) {
                specialOfferAnalytics.showOfferWithNewDesign(`discounts:${offerByQuery.title}`);
            }

            batch(() => {
                dispatch(setSelectedOfferId(offerByQuery.requestId));
                dispatch(setIsPopupOpened(true));
            });
        }
    }, [offers, offer_id, requestId]);

    const PreparedEmptyComponent = typeof EmptyComponent === 'function' ? EmptyComponent : DefaultPromoStub;

    return (
        <>
            {headlined
                ? <H3>{title}</H3>
                : <H4 as='h2'>{title}</H4>
            }
            <WithSkeletonContext isLoading={isLoading}>
                <ST.CarouselContainer>
                    <WithSkeleton
                        borderRadius={BorderRadius.OuterBlockRadius}
                        className={'skeleton-item'}
                        containerClassName={'skeletons-wrapper'}
                        count={3}
                        inline
                    >
                        {offers.length
                            ? <SpeedBonusConnection>
                                <LkPromotionalCardsCarousel
                                    isLoading={isLoading}
                                    offers={offers}
                                    showAllButton={showAllButton}
                                    root={root}
                                />
                            </SpeedBonusConnection>
                            : <PreparedEmptyComponent/>
                        }
                    </WithSkeleton>
                </ST.CarouselContainer>
            </WithSkeletonContext>
        </>
    );
};

export default SpecialOffersContainer;
